import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type {
  AntecipationType,
  ClienteType,
  ExtratoType,
  FaturaType,
  InvoiceInstallmentCalculationType,
  PaymentInvoiceType,
  SaldoType,
} from "models/cliente";
import {
  bloqueioCartaoCliente,
  getFaturaAtualCliente,
  getSaldoClienteTokenCartao,
  getTokenCartaoByNumero,
  postExtratoCartao,
  reemissaoCartaoCliente,
} from "services/http/cliente-service";
import { RootState } from "redux/app/store";
import {
  resetLoading,
  setLoading,
  setTitleLoading,
} from "redux/ui/loadings-slice";
import { showMessage } from "utils/messages";
import { CartaoBloqueioRequest, CartaoReemissaoRequest } from "models/cartao";
import { ResponseAdagaPinPad } from "views/Atendimento/Fatura/PaymentDebitCredit/FeedUserPOS/model/ModelResponseAdaga";

type ClienteState = {
  cliente: ClienteType;
  cartaoBloqueado?: boolean;
  segundaViaSucesso?: boolean;
  antecipation: AntecipationType[];
};

type TokenCartao = {
  token: string;
  tokenCartao: string;
};

const initialState: ClienteState = {
  cliente: {
    cpf: "",
    celular: "",
    valuePaymentInvoice: "",
    pinpadResponse: {},
    installmentPaymentInvoice: "",
    invoiceInstallmentCalculation: {
      detalhesParcelas: [
        {
          parcelas: 0,
          valor: 0,
          valorTaxaJuros: 0,
        },
      ],
      parcelas: 0,
      taxaJuros: 0,
      taxaServico: 0,
      valorTaxaServico: 0,
      valorTotal: 0,
    },
    faturaList: {
      data: {
        melhor_dia_compra: 0,
        status: "",
      },
    },
    dadosLogin: {
      numeroCartao: "",
      modeloTemaCartao: "",
    },
  },
  cartaoBloqueado: false,
  segundaViaSucesso: false,
  antecipation: [],
};

const handleSetCliente = (
  state: ClienteState,
  action: PayloadAction<ClienteType>,
) => {
  state.cliente = action.payload;
};

const handleCartaoCliente = (
  state: ClienteState,
  action: PayloadAction<string>,
) => {
  if (state.cliente.dadosLogin) {
    state.cliente.dadosLogin.numeroCartao = action.payload;
  }
};

const handleAntecipationList = (
  state: ClienteState,
  action: PayloadAction<AntecipationType[]>,
) => {
  state.cliente.antecipation = action.payload;
};

export const selectAntecipationList = (state: RootState) =>
  state.cliente.cliente.antecipation;

export const addCelular = createAsyncThunk(
  `cliente/setCelular`,
  async (celular: string) => {
    return celular;
  },
);

export const addValuePaymentInvoice = createAsyncThunk(
  `cliente/setValuePaymentInvoice`,
  async (valuePaymentInvoice: string) => {
    return valuePaymentInvoice;
  },
);

export const addValueAntecipationPurchase = createAsyncThunk(
  `cliente/setValueAntecipationPurchase`,
  async (valueAntecipationPurchase: number) => {
    return valueAntecipationPurchase;
  },
);

export const addValueDiscountAntecipationPurchase = createAsyncThunk(
  `cliente/setValueDiscountAntecipationPurchase`,
  async (valueDiscountAntecipationPurchase: number) => {
    return valueDiscountAntecipationPurchase;
  },
);

export const addPinpadResponse = createAsyncThunk(
  `cliente/setPinpadResponse`,
  async (pinpadResponse: ResponseAdagaPinPad) => {
    return pinpadResponse;
  },
);

export const addInvoiceInstallmentCalculation = createAsyncThunk(
  `cliente/setInvoiceInstallmentCalculation`,
  async (invoiceInstallmentCalculation: InvoiceInstallmentCalculationType) => {
    return invoiceInstallmentCalculation;
  },
);

export const addPaymentInvoice = createAsyncThunk(
  `cliente/setPaymentInvoice`,
  async (paymentInvoice: PaymentInvoiceType) => {
    return paymentInvoice;
  },
);

export const addFaturaList = createAsyncThunk(
  `cliente/setFaturaList`,
  async (faturaList: FaturaType) => {
    return faturaList;
  },
);

export const addAntecipationList = createAsyncThunk(
  `client/setAntecipationPurchase`,
  async (antecipation: AntecipationType) => {
    return antecipation;
  },
);

export const addInstallmentPaymentInvoice = createAsyncThunk(
  `cliente/setInstallmentPaymentInvoice`,
  async (installmentPaymentInvoice: string) => {
    return installmentPaymentInvoice;
  },
);

export const fetchFaturaCliente = createAsyncThunk(
  `cliente/faturaCartao`,
  async (props: string, { dispatch }) => {
    dispatch(setLoading(true));
    dispatch(setTitleLoading("Carregando dados da tua fatura atual..."));
    const response = (await getFaturaAtualCliente(props)
      .then((res) => res.data)
      .catch((error) => {
        dispatch(resetLoading());
        const { mensagem } = error.response.data;
        showMessage("warning", "Atenção!", mensagem);
      })) as FaturaType;
    return response;
  },
);

export const fetchSaldoCliente = createAsyncThunk(
  `cliente/saldoCartao`,
  async (props, { getState, dispatch }) => {
    let saldo: SaldoType = {
      limiteUtilizado: 0,
      saldo: 0,
    };
    const {
      cliente: {
        cliente: { cartaoAtual },
      },
    } = (await getState()) as RootState;
    if (cartaoAtual) {
      dispatch(setLoading(true));
      dispatch(setTitleLoading("Carregando dados do seu cartão..."));
      const response = (await getTokenCartaoByNumero(cartaoAtual.numeroCartao || "")
        .then((res) => res.data)
        .catch((error) => {
          dispatch(resetLoading());
          const { mensagem } = error.response.data;
          showMessage("warning", "Atenção!", mensagem);
        })) as TokenCartao;

      if (response) {
        const responseSaldo = await getSaldoClienteTokenCartao(
          response.tokenCartao,
        )
          .then((res) => res.data)
          .catch((error) => {
            dispatch(resetLoading());
            const { mensagem } = error.response.data;
            showMessage("warning", "Atenção!", mensagem);
          });
        dispatch(resetLoading());
        saldo = responseSaldo as SaldoType;
      }
      dispatch(resetLoading());
    }
    return saldo;
  },
);

export const fetchReemissaoCartao = createAsyncThunk(
  `cliente/reemissaoCartao`,
  async (props: CartaoReemissaoRequest, { dispatch }) => {
    const response = await reemissaoCartaoCliente(props)
      .then(() => {
        dispatch(resetLoading());
        dispatch(cleanState());
        showMessage(
          "success",
          "Segunda via solicitada!",
          "Aguarde para prosseguir",
          { timer: 2000, showConfirmButton: false },
        );
        return true;
      })
      .catch((error) => {
        dispatch(resetLoading());
        const mensagem = error?.response?.data?.mensagem;
        showMessage("warning", "Atenção!", mensagem);
        return false;
      });
    return response;
  },
);

export const fetchBloqueiaCartao = createAsyncThunk(
  `cliente/bloqueaCartao`,
  async (props: CartaoBloqueioRequest, { dispatch }) => {
    const response = await bloqueioCartaoCliente(props)
      .then((res) => {
        dispatch(resetLoading());
        showMessage(
          "success",
          "Cartão bloqueado!",
          "Seu cartão foi bloqueado com sucesso, aguarde para prosseguir.",
          { timer: 2000, showConfirmButton: false },
        );
        return true;
      })
      .catch((error) => {
        dispatch(resetLoading());
        const { mensagem } = error.response.data;
        showMessage("warning", "Atenção!", mensagem);
        return false;
      });
    return response;
  },
);

export const fetchExtratoCartao = createAsyncThunk(
  `cliente/ExtratoCartao`,
  async (props: any, { dispatch }) => {
    dispatch(setLoading(true));
    dispatch(setTitleLoading("Carregando dados do seu extrato..."));
    const response = (await postExtratoCartao(props)
      .then((res) => {
        dispatch(resetLoading());
        return res.data;
      })
      .catch((error) => {
        dispatch(resetLoading());
        const { mensagem } = error.response.data;
        showMessage("warning", "Atenção!", mensagem);
      })) as ExtratoType;
    return response;
  },
);

export const clienteSlices = createSlice({
  name: "cliente",
  initialState: { ...initialState },
  reducers: {
    setCliente: handleSetCliente,
    setCartao: handleCartaoCliente,
    cleanState: (state) => initialState,
    setAntecipationList: handleAntecipationList,
  },
  extraReducers: (builder) => {
    builder.addCase(fetchBloqueiaCartao.fulfilled, (state, action) => {
      state.cartaoBloqueado = action.payload;
    });

    builder.addCase(fetchReemissaoCartao.fulfilled, (state, action) => {
      state.segundaViaSucesso = action.payload;
    });

    builder.addCase(fetchSaldoCliente.fulfilled, (state, action) => {
      state.cliente.saldo = action.payload as SaldoType;
    });

    builder.addCase(addFaturaList.fulfilled, (state, action) => {
      state.cliente.faturaList = action.payload;
    });

    builder.addCase(fetchFaturaCliente.fulfilled, (state, action) => {
      state.cliente.faturaList = action.payload;
    });

    builder.addCase(fetchExtratoCartao.fulfilled, (state, action) => {
      state.cliente.extrato = action.payload;
    });

    builder.addCase(addCelular.fulfilled, (state, action) => {
      state.cliente.celular = action.payload;
    });

    builder.addCase(addValuePaymentInvoice.fulfilled, (state, action) => {
      state.cliente.valuePaymentInvoice = action.payload;
    });

    builder.addCase(addValueAntecipationPurchase.fulfilled, (state, action) => {
      state.cliente.valueAntecipationPurchase = action.payload;
    });

    builder.addCase(
      addValueDiscountAntecipationPurchase.fulfilled,
      (state, action) => {
        state.cliente.valueDiscountAntecipationPurchase = action.payload;
      },
    );

    builder.addCase(addPinpadResponse.fulfilled, (state, action) => {
      state.cliente.pinpadResponse = action.payload;
    });

    builder.addCase(
      addInvoiceInstallmentCalculation.fulfilled,
      (state, action) => {
        state.cliente.invoiceInstallmentCalculation = action.payload;
      },
    );

    builder.addCase(addPaymentInvoice.fulfilled, (state, action) => {
      state.cliente.paymentInvoice = action.payload;
    });

    builder.addCase(addInstallmentPaymentInvoice.fulfilled, (state, action) => {
      state.cliente.installmentPaymentInvoice = action.payload;
    });
  },
});

export const { setCliente, setCartao, cleanState } = clienteSlices.actions;
export default clienteSlices.reducer;
